import { useCallback, useEffect, useMemo, useState } from "react";
import { LoaderCircle, RefreshCw, UserPlus } from "lucide-react";
import { BrowserRouter, useParams } from "react-router-dom";

import { LEVEL_REGISTRY } from "@/content/registry";
import { createProgressRepository } from "@/data";
import type { Domain } from "@/domain";
import { AudioProvider, AudioStatusControl } from "@/features/audio";
import { IslandMapScreen } from "@/features/island-map";
import {
  useProfileSession,
  type ProfileRepositoryBootstrap,
  type ProfileRepositoryGeneration,
  type ProfileRepositorySession,
  type ProfileSessionStatus
} from "@/features/navigation";
import { OnboardingScreen } from "@/features/onboarding";
import {
  ParentCenter,
  type ParentAccessStore,
  type ParentClearAllOutcome
} from "@/features/parent";
import { parentAccessStore as defaultParentAccessStore } from "@/platform/parent-access";

import {
  AppShell,
  type AppShellProps,
  type AppShellSlots
} from "./AppShell";
import type {
  AppNavigation,
  AppPlayAdapter,
  AppPlayAdapterProps
} from "./integration/contracts";
import { DefaultPlayAdapter } from "./integration/DefaultPlayAdapter";
import {
  loadRepositoryMapData,
  resolveDomainLevelId,
  resolveRegisteredLevel,
  type AppMapData,
  type RepositoryMapData
} from "./integration/map-levels";
import { useProfileSessionRouter } from "./integration/profile-session-router";
import { createStableProfileRepositoryBootstrap } from "./integration/repository-bootstrap";

const DEFAULT_REPOSITORY_GENERATION = "app-progress-repository-v1";

async function bootstrapDefaultRepository(): Promise<ProfileRepositorySession> {
  const handle = await createProgressRepository();

  return {
    persistenceMode: handle.isPersistent ? "local" : "temporary",
    repository: handle.repository
  };
}

export interface AppProps extends AppShellProps {
  readonly bootstrapRepository?: ProfileRepositoryBootstrap;
  readonly mapData?: AppMapData;
  readonly parentAccessStore?: ParentAccessStore;
  readonly playAdapter?: AppPlayAdapter;
  readonly repositoryGeneration?: ProfileRepositoryGeneration;
}

interface AppCompositionProps extends AppShellProps {
  readonly bootstrapRepository: ProfileRepositoryBootstrap;
  readonly mapData?: AppMapData;
  readonly parentAccessStore: ParentAccessStore;
  readonly playAdapter?: AppPlayAdapter;
  readonly repositoryGeneration: ProfileRepositoryGeneration;
}

interface ParentRepositoryStateProps {
  readonly onRetry: () => void;
  readonly status: ProfileSessionStatus;
}

interface ParentEmptyStateProps {
  readonly clearWarning: string | null;
  readonly onCreateProfile: () => void;
}

interface ParentClearWarningState {
  readonly repositoryGeneration: number;
  readonly warning: string;
}

type RepositoryMapLoadState =
  | { readonly status: "idle" }
  | {
      readonly profileId: string;
      readonly repositoryGeneration: number;
      readonly status: "error";
    }
  | {
      readonly data: RepositoryMapData;
      readonly profileId: string;
      readonly repositoryGeneration: number;
      readonly status: "ready";
    };

function ParentRepositoryState({
  onRetry,
  status
}: ParentRepositoryStateProps) {
  const failed = status === "error";

  return (
    <section
      aria-busy={!failed}
      className="app-shell__placeholder"
      role={failed ? "alert" : "status"}
    >
      {failed ? (
        <RefreshCw aria-hidden="true" size={36} strokeWidth={2.5} />
      ) : (
        <LoaderCircle aria-hidden="true" size={36} strokeWidth={2.5} />
      )}
      <p className="app-shell__eyebrow">仅限家长</p>
      <h1>家长空间</h1>
      <p>{failed ? "本机资料暂时打不开。" : "正在读取本机资料..."}</p>
      {failed ? (
        <button
          className="app-shell__primary-action"
          onClick={onRetry}
          type="button"
        >
          <RefreshCw aria-hidden="true" size={24} strokeWidth={2.5} />
          再试一次
        </button>
      ) : null}
    </section>
  );
}

function ParentEmptyState({
  clearWarning,
  onCreateProfile
}: ParentEmptyStateProps) {
  return (
    <section className="app-shell__placeholder">
      <p className="app-shell__eyebrow">仅限家长</p>
      <h1>家长空间</h1>
      <p>还没有小船员资料，可以先回到启航名单新建一位。</p>
      {clearWarning === null ? null : (
        <p role="alert">{clearWarning}</p>
      )}
      <button
        className="app-shell__primary-action"
        onClick={onCreateProfile}
        type="button"
      >
        <UserPlus aria-hidden="true" size={24} strokeWidth={2.5} />
        新建小船员
      </button>
    </section>
  );
}

interface PlayAdapterRouteProps {
  readonly Adapter: AppPlayAdapter;
  readonly navigation: AppNavigation;
  readonly persistenceMode: AppPlayAdapterProps["persistenceMode"];
  readonly repository: AppPlayAdapterProps["repository"] | null;
  readonly repositoryGeneration: number;
  readonly selectedProfile: AppPlayAdapterProps["selectedProfile"] | null;
  readonly status: ProfileSessionStatus;
}

function PlayAdapterRoute({
  Adapter,
  navigation,
  persistenceMode,
  repository,
  repositoryGeneration,
  selectedProfile,
  status
}: PlayAdapterRouteProps) {
  const { levelId } = useParams<"levelId">();

  if (repository === null || selectedProfile === null || status !== "ready") {
    return (
      <section
        aria-busy="true"
        className="app-shell__placeholder"
        role="status"
      >
        <LoaderCircle aria-hidden="true" size={36} strokeWidth={2.5} />
        <p className="app-shell__eyebrow">学习活动</p>
        <h1>准备出发</h1>
      </section>
    );
  }

  return (
    <Adapter
      levelId={levelId ?? null}
      navigation={navigation}
      persistenceMode={persistenceMode}
      repository={repository}
      repositoryGeneration={repositoryGeneration}
      selectedProfile={selectedProfile}
    />
  );
}

function AppComposition({
  audioControl = <AudioStatusControl />,
  bootstrapRepository,
  homePath,
  mapData,
  parentAccessStore,
  playAdapter: PlayAdapter,
  repositoryGeneration,
  slots = {},
  status
}: AppCompositionProps) {
  const router = useProfileSessionRouter();
  const session = useProfileSession({
    bootstrapRepository,
    navigate: router.navigate,
    repositoryGeneration,
    route: router.route
  });
  const [parentClearWarning, setParentClearWarning] =
    useState<ParentClearWarningState | null>(null);
  const [repositoryMapState, setRepositoryMapState] =
    useState<RepositoryMapLoadState>({ status: "idle" });
  const visibleParentClearWarning =
    parentClearWarning?.repositoryGeneration === session.repositoryGeneration
      ? parentClearWarning.warning
      : null;
  const selectedProfileId = session.selectedProfile?.id ?? null;

  useEffect(() => {
    if (
      router.route.kind !== "map" ||
      session.repository === null ||
      selectedProfileId === null ||
      session.status !== "ready"
    ) {
      return;
    }

    let cancelled = false;
    const activeRepository = session.repository;
    const activeGeneration = session.repositoryGeneration;

    void loadRepositoryMapData(
      activeRepository,
      selectedProfileId,
      LEVEL_REGISTRY
    ).then(
      (data) => {
        if (!cancelled) {
          setRepositoryMapState({
            data,
            profileId: selectedProfileId,
            repositoryGeneration: activeGeneration,
            status: "ready"
          });
        }
      },
      () => {
        if (!cancelled) {
          setRepositoryMapState({
            profileId: selectedProfileId,
            repositoryGeneration: activeGeneration,
            status: "error"
          });
        }
      }
    );

    return () => {
      cancelled = true;
    };
  }, [
    router.route.kind,
    selectedProfileId,
    session.repository,
    session.repositoryGeneration,
    session.status
  ]);

  const activeRepositoryMapState =
    repositoryMapState.status !== "idle" &&
    repositoryMapState.profileId === selectedProfileId &&
    repositoryMapState.repositoryGeneration === session.repositoryGeneration
      ? repositoryMapState
      : null;
  const repositoryMapData =
    activeRepositoryMapState?.status === "ready"
      ? activeRepositoryMapState.data
      : null;
  const repositoryMapSettled =
    mapData !== undefined ||
    activeRepositoryMapState?.status === "ready" ||
    activeRepositoryMapState?.status === "error";
  const effectiveMapData: AppMapData = {
    dailyRecommendation:
      mapData?.dailyRecommendation !== undefined
        ? mapData.dailyRecommendation
        : repositoryMapData?.dailyRecommendation,
    decorations: mapData?.decorations ?? repositoryMapData?.decorations,
    progress: mapData?.progress ?? repositoryMapData?.progress
  };
  const handleSelectDomain = useCallback(
    (domain: Domain) => {
      const preferredLevelId =
        mapData?.dailyRecommendation?.domain === domain
          ? mapData.dailyRecommendation.levelId
          : repositoryMapData?.preferredLevelIds[domain] ?? null;
      const levelId = resolveDomainLevelId(
        LEVEL_REGISTRY,
        domain,
        preferredLevelId
      );

      if (levelId !== null) {
        session.navigation.showLevel(levelId);
      }
    },
    [mapData, repositoryMapData, session.navigation]
  );
  const handleSelectLevel = useCallback(
    (levelId: string) => {
      const level = resolveRegisteredLevel(LEVEL_REGISTRY, levelId);

      if (level !== null) {
        session.navigation.showLevel(level.id);
      }
    },
    [session.navigation]
  );
  const handleAllProfilesCleared = useCallback(
    (outcome: ParentClearAllOutcome) => {
      session.allProfilesCleared();
      setParentClearWarning(
        outcome.status === "parent-access-retained"
          ? {
              repositoryGeneration: session.repositoryGeneration,
              warning: outcome.warning
            }
          : null
      );
    },
    [session]
  );
  const parentProfileId =
    session.selectedProfile?.id ?? session.profiles[0]?.id ?? null;
  const parentSlot =
    session.repository === null ? (
      <ParentRepositoryState
        onRetry={session.retryProfiles}
        status={session.status}
      />
    ) : parentProfileId !== null ? (
      <ParentCenter
        onAllProfilesCleared={handleAllProfilesCleared}
        onProfileDeleted={session.profileDeleted}
        parentAccessStore={parentAccessStore}
        profileId={parentProfileId}
        repository={session.repository}
      />
    ) : session.status === "ready" ? (
      <ParentEmptyState
        clearWarning={visibleParentClearWarning}
        onCreateProfile={session.navigation.showOnboarding}
      />
    ) : (
      <ParentRepositoryState
        onRetry={session.retryProfiles}
        status={session.status}
      />
    );
  const playSlot =
    slots.play ??
    (PlayAdapter === undefined ? undefined : (
      <PlayAdapterRoute
        Adapter={PlayAdapter}
        navigation={session.navigation}
        persistenceMode={session.persistenceMode}
        repository={session.repository}
        repositoryGeneration={session.repositoryGeneration}
        selectedProfile={session.selectedProfile}
        status={session.status}
      />
    ));
  const composedSlots: AppShellSlots = {
    map: slots.map ?? (
      <IslandMapScreen
        {...effectiveMapData}
        isLoading={
          session.status !== "ready" ||
          session.selectedProfile === null ||
          !repositoryMapSettled
        }
        isTemporaryMode={session.persistenceMode === "temporary"}
        onSelectDomain={handleSelectDomain}
        onSelectLevel={handleSelectLevel}
      />
    ),
    onboarding: slots.onboarding ?? (
      <OnboardingScreen
        collectionError={session.error}
        collectionStatus={session.status}
        navigation={session.navigation}
        onRetryProfiles={session.retryProfiles}
        onSaveAndSelectProfile={session.saveAndSelectProfile}
        onSelectProfile={session.selectProfile}
        persistenceMode={session.persistenceMode}
        profiles={session.profiles}
        saveError={session.saveError}
        saveStatus={session.saveStatus}
        selectedProfile={session.selectedProfile}
      />
    ),
    parent: slots.parent ?? parentSlot,
    play: playSlot
  };

  return (
    <AppShell
      audioControl={audioControl}
      homePath={homePath}
      slots={composedSlots}
      status={{
        persistence: session.persistenceMode,
        profileName: session.selectedProfile?.nickname ?? null,
        ...status
      }}
    />
  );
}

export function App({
  bootstrapRepository = bootstrapDefaultRepository,
  mapData,
  parentAccessStore = defaultParentAccessStore,
  playAdapter = DefaultPlayAdapter,
  repositoryGeneration = DEFAULT_REPOSITORY_GENERATION,
  ...shellProps
}: AppProps) {
  const repositorySource = useMemo(
    () => ({
      bootstrapRepository:
        createStableProfileRepositoryBootstrap(bootstrapRepository),
      generation: repositoryGeneration
    }),
    [bootstrapRepository, repositoryGeneration]
  );

  return (
    <AudioProvider>
      <BrowserRouter>
        <AppComposition
          {...shellProps}
          bootstrapRepository={repositorySource.bootstrapRepository}
          mapData={mapData}
          parentAccessStore={parentAccessStore}
          playAdapter={playAdapter}
          repositoryGeneration={repositorySource.generation}
        />
      </BrowserRouter>
    </AudioProvider>
  );
}
